import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useMemo, useRef } from 'react'

export type SearchResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type TenantMemberSearchFn = (params: { search?: string }) => Promise<SearchResults>

const useSearchTenantMembersAndTeams = (
  search: string,
  selectedMembersIds: string[] = [],
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>,
  delegatorId?: string,
) => {
  const lastMatches = useRef<FederatedSearchRecipientsResults | undefined>(undefined)

  const {
    data: matches,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: ['searchTenantMembersAndTeams', search, delegatorId],
    queryFn: async () => {
      const result = await searchFn({ search, delegatorId })
      return result
    },
    placeholderData: lastMatches.current,
    enabled: !isEmpty(search),
  })

  if (isSuccess) {
    lastMatches.current = matches
  }

  const searchedTenantMembersAndTeams = useMemo(() => {
    if (isEmpty(search) || !matches) {
      return []
    }

    const teamMatches =
      matches.teams?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const memberMatches =
      matches.tenantMembers?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const allMatches = [...teamMatches, ...memberMatches]
    return allMatches
  }, [matches, search])

  return {
    searchedTenantMembersAndTeams,
    isFetching,
  }
}

export default useSearchTenantMembersAndTeams
